/**
 * @file index.jsx
 * @brief Displays a public user profile with active listings and optional profile editing controls.
 */
import { useState, useEffect } from "react";
import { useUnifiedAuth as useAuth } from "../../hooks/useUnifiedAuth";
import { useTranslation } from "react-i18next";
import { createApiClient } from "../../api/axios";
import { useParams, Link } from "react-router-dom";
import { useUserProfile } from "../../context/UserProfileContext";
import { truncateText } from "../../utils/truncateText";
import styles from "./user-profile.module.css";

/**
 * Render a user profile page, including listings and editable profile fields when authorized.
 * @returns {JSX.Element} The profile screen for the requested user.
 */
export default function UserProfile() {
    const { t } = useTranslation();

    const auth = useAuth();
    const accessToken = auth.user?.access_token;
    const isAuthenticated = auth.isAuthenticated;
    const { userId } = useParams();

    const [listings, setListings] = useState([]);
    const [imagesMap, setImagesMap] = useState({});
    const [userProfile, setUserProfile] = useState(null);
    const [avatarUrl, setAvatarUrl] = useState(null);

    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const { profile } = useUserProfile();

    const [editing, setEditing] = useState(false);
    const [bioDraft, setBioDraft] = useState("");
    const [newAvatar, setNewAvatar] = useState(null);

    const [previewAvatar, setPreviewAvatar] = useState(null);

    const isModerator = profile?.groups?.includes("moderators");
    const isAdmin = profile?.groups?.includes("admins");

    const canEditProfile = isModerator || isAdmin || (String(profile?.id) === String(userId));

    /**
        * Store a newly selected avatar file and preview URL before saving profile changes.
        * @param {Event} e File input change event.
        * @returns {void}
     */
    const handleAvatarChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setNewAvatar(file);
            setPreviewAvatar(URL.createObjectURL(file));
        }
    };
    const saveProfile = async () => {
        try {
            const api = createApiClient({
                isAuthenticated,
                user: accessToken ? { access_token: accessToken } : null,
            });
            const formData = new FormData();

            formData.append("bio", bioDraft);

            if (newAvatar) {
                formData.append("profile_picture", newAvatar);
            }

            const res = await api.patch(`userprofiles/${userId}/`, formData, {
                headers: { "Content-Type": "multipart/form-data" },
            });

            setUserProfile(res.data);
            setEditing(false);

            // refresh avatar if changed
            if (newAvatar && res.data.profile_picture_url) {
                const img = await fetch(res.data.profile_picture_url, {
                    headers: {
                        Authorization: `Bearer ${accessToken}`,
                    },
                });

                const blob = await img.blob();
                setAvatarUrl(URL.createObjectURL(blob));
            }

        } catch (err) {
            console.error("Profile update failed", err);
        }
    };
    useEffect(() => {
        const fetchData = async () => {
            setLoading(true);

            try {
                const api = createApiClient({
                    isAuthenticated,
                    user: accessToken ? { access_token: accessToken } : null,
                });
                // Fetch profile
                const profileRes = await api.get(`userprofiles/${userId}/`);
                const profileData = profileRes.data;
                setUserProfile(profileData);
                setBioDraft(profileData.bio || "");
                // Fetch avatar if available
                if (profileData.profile_picture_url) {
                    try {
                        const res = await fetch(profileData.profile_picture_url, {
                            headers: {
                                Authorization: `Bearer ${accessToken}`,
                            },
                        });

                        if (res.ok) {
                            const blob = await res.blob();
                            setAvatarUrl(URL.createObjectURL(blob));
                        }
                    } catch (err) {
                        console.error("Avatar fetch failed", err);
                    }
                }

                // Fetch listings
                const listingsRes = await api.get("listings/", {
                    params: { author: userId, status: "active" },
                });
                const listingsData = listingsRes.data.results;
                setListings(listingsData);

                // Fetch first images of listings
                const map = {};
                await Promise.all(
                    listingsData.map(async (listing) => {
                        if (!listing.images?.length) return;
                        try {
                            const imgRes = await fetch(listing.images[0].url, {
                                headers: { Authorization: `Bearer ${accessToken}` },
                            });
                            if (!imgRes.ok) return;
                            const blob = await imgRes.blob();
                            map[listing.id] = URL.createObjectURL(blob);
                        } catch (err) {
                            console.error("Listing image fetch failed", err);
                        }
                    })
                );
                setImagesMap(map);

            } catch (err) {
                setError(err);
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [accessToken, isAuthenticated, userId]);

    if (!auth.isAuthenticated) return <div className={styles.center}>{t("userProfile.loginRequired")}</div>;
    if (loading) return <div className={styles.center}>{t("userProfile.loading")}</div>;
    if (error) return <div className={styles.center}>{t("userProfile.error", { message: error.message })}</div>;

    return (
        <div className={styles.container}>

            {/* Profile Header */}
            <div className={styles.profileCard}>
                <div
                    className={styles.avatar}
                    onClick={() => canEditProfile && setEditing(true)}
                >
                    {avatarUrl ? (
                        <img
                            src={avatarUrl}
                            alt={`${userProfile.user.username} avatar`}
                            className={styles.avatarImage}
                        />
                    ) : (
                        userProfile?.user.username[0].toUpperCase()
                    )}
                </div>

                <div className={styles.profileInfo}>
                    <h1>
                        {userProfile?.user.first_name} {userProfile?.user.last_name}
                    </h1>

                    <p className={styles.username}>
                        @{userProfile?.user.username}
                    </p>
                    <div className={styles.bioContainer}>

                        <p className={styles.bio}>{userProfile?.bio}</p>

                    </div>
                    <p className={styles.meta}>
                        {t("userProfile.activeListings", { count: listings.length })}
                    </p>
                </div>
                {canEditProfile && (
                    <button
                        className={styles.editBtn}
                        onClick={() => setEditing(true)}
                    >
                        {t("userProfile.editProfile")}
                    </button>
                )}
                {editing && (
                    <div
                        className={styles.modalOverlay}
                        onClick={() => setEditing(false)}
                    >
                        <div
                            className={styles.modal}
                            onClick={(e) => e.stopPropagation()}
                        >
                            <div className={styles.modalHeader}>
                                {t("userProfile.editProfile")}
                            </div>

                            <div className={styles.avatarEditor}>
                                <div className={styles.avatarPreview}>
                                    <img
                                        src={previewAvatar || avatarUrl}
                                        alt={t("userProfile.avatarPreview")}
                                    />
                                </div>

                                <input
                                    className={styles.fileInput}
                                    type="file"
                                    accept="image/*"
                                    onChange={handleAvatarChange}
                                />
                            </div>

                            <label>{t("account.bio")}</label>
                            <textarea
                                value={bioDraft}
                                onChange={(e) => setBioDraft(e.target.value)}
                                className={styles.bioEditor}
                            />

                            <div className={styles.modalActions}>
                                <button
                                    className={styles.cancelBtn}
                                    onClick={() => setEditing(false)}
                                >
                                    {t("common.cancel")}
                                </button>

                                <button
                                    className={styles.saveBtn}
                                    onClick={saveProfile}
                                >
                                    {t("userProfile.saveChanges")}
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>

            {/* Listings */}
            <div className={styles.section}>
                {listings.length === 0 ? (
                    <p className={styles.empty}>{t("userProfile.noActiveListings")}</p>
                ) : (
                    <div className={styles.grid}>
                        {listings.map((listing) => (
                            <Link
                                key={listing.id}
                                to={`/listings/${listing.id}`}
                                className={styles.card}
                            >

                                <div className={styles.imageWrapper}>
                                    {imagesMap[listing.id] ? (
                                        <img
                                            src={imagesMap[listing.id]}
                                            alt={listing.title}
                                            className={styles.image}
                                        />
                                    ) : (
                                        <div className={styles.noImage}>
                                            {t("common.noImage")}
                                        </div>
                                    )}
                                </div>

                                <h3 className={styles.listingTitle} title={listing.title}>{truncateText(listing.title)}</h3>



                                <div className={styles.price}>
                                    {listing.price} €
                                </div>

                            </Link>
                        ))}
                    </div>
                )}
            </div>

        </div>
    );
}